import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@/components/icons";
import type {
  ProductCarouselImage,
  ProductCarouselItem,
} from "@/types/product";
import { useLocalizedTexts, t } from "@/utils/translations";
import {
  LCP_IMAGE_PRIORITY,
  fixedCmsImage,
  responsiveCmsImage,
} from "@/utils/cmsImage";
import styles from "./ProductMediaGallery.module.css";

interface ProductMediaGalleryProps {
  items: ProductCarouselItem[];
  productName: string;
}

const THUMBNAIL_TARGET_WIDTH = 88;
const THUMBNAIL_GAP = 8;

/**
 * The gallery fills the product page's 6-of-12 block grid column (572px of the
 * 1168px grid) and takes the full width once the grid stacks at 1080px.
 */
const MAIN_IMAGE_SIZES =
  "(max-width: 1080px) 100vw, (max-width: 1200px) 50vw, 572px";

function PlayOverlay() {
  return (
    <span className={styles.playOverlay} aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5v14l11-7z" />
      </svg>
    </span>
  );
}

export const ProductMediaGallery: React.FC<ProductMediaGalleryProps> = ({
  items,
  productName,
}) => {
  const localizedTexts = useLocalizedTexts();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [thumbnailOffset, setThumbnailOffset] = useState(0);
  const [visibleCount, setVisibleCount] = useState(1);
  const [containerWidth, setContainerWidth] = useState(0);
  const [videoControlsVisible, setVideoControlsVisible] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const thumbnailListRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  // The image last shown in full in the main slot. A newly selected image
  // loads hidden on top of it and takes its place once loaded, so the current
  // image stays on screen instead of the slot going blank during the download.
  const [settledImage, setSettledImage] = useState<ProductCarouselImage | null>(
    null
  );
  const preloads = useRef(new Map<string, HTMLImageElement>());

  const selectedItem = items[selectedIndex] ?? items[0];

  const settleImage = (item: ProductCarouselImage) =>
    setSettledImage((prev) => (prev?.id === item.id ? prev : item));

  // A video takes the slot at once, and the image after it starts from a blank
  // slot rather than from an image two steps back.
  useEffect(() => {
    if (selectedItem?.kind !== "image") setSettledImage(null);
  }, [selectedItem]);

  // The main slot shows a resized file (the thumbnails are small versions),
  // and the server takes ~0.7 s to make a size nobody has asked for yet. So
  // the file starts loading as soon as the visitor heads for an image: a
  // thumbnail or arrow hovered, touched or focused, a swipe starting.
  const preloadMainImage = (index: number) => {
    if (!items.length) return;
    const item = items[(index + items.length) % items.length];
    if (item?.kind !== "image" || preloads.current.has(item.url)) return;
    const sources = responsiveCmsImage(item.url, MAIN_IMAGE_SIZES, item.width);
    const img = new Image();
    // sizes and srcset before src: the browser then picks the candidate the
    // main image will, and fetches nothing else. Kept in the map so the
    // download is not dropped with a collected Image.
    if (sources.sizes) img.sizes = sources.sizes;
    if (sources.srcSet) img.srcset = sources.srcSet;
    img.src = sources.src;
    preloads.current.set(item.url, img);
  };

  const goTo = (next: number) => {
    if (!items.length) return;
    const wrapped = (next + items.length) % items.length;
    setSelectedIndex(wrapped);
  };

  // Keep selectedIndex valid when the items list shrinks.
  useEffect(() => {
    setSelectedIndex((prev) => Math.min(prev, Math.max(0, items.length - 1)));
  }, [items.length]);

  // When the selected item changes, reset any inline video playback state.
  useEffect(() => {
    setVideoControlsVisible(false);
    setIsVideoPlaying(false);
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.currentTime = 0;
    }
  }, [selectedIndex]);

  const updateVisibleCount = useCallback(() => {
    if (!thumbnailListRef.current) return;
    const width = thumbnailListRef.current.clientWidth;
    setContainerWidth(width);
    const count = Math.max(
      1,
      Math.floor(
        (width + THUMBNAIL_GAP) / (THUMBNAIL_TARGET_WIDTH + THUMBNAIL_GAP)
      )
    );
    setVisibleCount(count);
  }, []);

  // Measured before paint: the thumbnails are sized from this width, and a
  // first paint at width 0 grew the strip under the image, pushing the title,
  // price and buy button down (CLS).
  const hasThumbnails = items.length > 1;
  useLayoutEffect(() => {
    const el = thumbnailListRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => updateVisibleCount());
    observer.observe(el);
    updateVisibleCount();
    return () => observer.disconnect();
  }, [updateVisibleCount, hasThumbnails]);

  useEffect(() => {
    if (thumbnailOffset + visibleCount > items.length) {
      setThumbnailOffset(Math.max(0, items.length - visibleCount));
    }
  }, [visibleCount, items.length, thumbnailOffset]);

  const thumbnailSize =
    visibleCount > 0
      ? (containerWidth - (visibleCount - 1) * THUMBNAIL_GAP) / visibleCount
      : THUMBNAIL_TARGET_WIDTH;
  const translateX = thumbnailOffset * (thumbnailSize + THUMBNAIL_GAP);

  const canScrollLeft = thumbnailOffset > 0;
  const canScrollRight = thumbnailOffset + visibleCount < items.length;

  const handlePrevThumbnails = () =>
    setThumbnailOffset((prev) => Math.max(0, prev - 1));
  const handleNextThumbnails = () =>
    setThumbnailOffset((prev) =>
      Math.min(items.length - visibleCount, prev + 1)
    );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      goTo(selectedIndex - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      goTo(selectedIndex + 1);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (!t) return;
    touchStart.current = { x: t.clientX, y: t.clientY };
    preloadMainImage(selectedIndex - 1);
    preloadMainImage(selectedIndex + 1);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const start = touchStart.current;
    const end = e.changedTouches[0];
    if (!start || !end) return;
    const dx = end.clientX - start.x;
    const dy = end.clientY - start.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      goTo(selectedIndex + (dx < 0 ? 1 : -1));
    }
    touchStart.current = null;
  };

  const handleMainMouseEnter = () => {
    if (selectedItem?.kind !== "video") return;
    if (videoControlsVisible) return;
    videoRef.current?.play().catch(() => {});
  };

  const handleMainMouseLeave = () => {
    if (selectedItem?.kind !== "video") return;
    if (videoControlsVisible) return;
    videoRef.current?.pause();
  };

  const handleOverlayClick = () => {
    const v = videoRef.current;
    if (!v) return;
    setVideoControlsVisible(true);
    if (v.paused) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  };

  const outgoingImage =
    selectedItem?.kind === "image" &&
    settledImage &&
    settledImage.id !== selectedItem.id
      ? settledImage
      : null;

  if (!items.length) {
    return (
      <div className={styles.gallery}>
        <div className={styles.mainImage}>
          <div className={styles.mainImagePlaceholder} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.gallery}>
      {/* Main media */}
      <div
        className={styles.mainImage}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseEnter={handleMainMouseEnter}
        onMouseLeave={handleMainMouseLeave}
        role="group"
        aria-label={t(
          localizedTexts,
          "Tracker.Products.ImageGallery",
          "Product image gallery — use arrow keys to navigate"
        )}
      >
        {selectedItem?.kind === "image" && (
          <>
            {outgoingImage && (
              <img
                key={outgoingImage.id}
                {...responsiveCmsImage(
                  outgoingImage.url,
                  MAIN_IMAGE_SIZES,
                  outgoingImage.width
                )}
                alt=""
                aria-hidden="true"
              />
            )}
            <img
              key={selectedItem.id}
              {...responsiveCmsImage(
                selectedItem.url,
                MAIN_IMAGE_SIZES,
                selectedItem.width
              )}
              alt={selectedItem.altText || productName}
              className={outgoingImage ? styles.incomingImage : undefined}
              onLoad={() => settleImage(selectedItem)}
              onError={() => settleImage(selectedItem)}
              {...LCP_IMAGE_PRIORITY}
            />
          </>
        )}
        {selectedItem?.kind === "video" && (
          <>
            <video
              ref={videoRef}
              key={selectedItem.id}
              className={styles.mainVideo}
              src={selectedItem.url}
              preload="auto"
              muted
              playsInline
              controls={videoControlsVisible}
              onPlay={() => setIsVideoPlaying(true)}
              onPause={() => setIsVideoPlaying(false)}
              aria-label={
                selectedItem.altText ||
                t(localizedTexts, "Tracker.Products.PlayVideo", "Play video")
              }
            >
              {/* TODO: Placeholder track for accessibility. Wire up real
                  caption support (src/srcLang/label) once caption files are
                  available from the CMS. */}
              <track kind="captions" />
            </video>
            {!videoControlsVisible && (
              <button
                type="button"
                className={styles.videoClickCapture}
                onClick={handleOverlayClick}
                aria-label={t(
                  localizedTexts,
                  "Tracker.Products.PlayVideo",
                  "Play video"
                )}
              >
                {!isVideoPlaying && (
                  <span className={styles.mainPlayOverlay} aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                )}
              </button>
            )}
          </>
        )}

        {items.length > 1 && (
          <>
            <button
              type="button"
              className={`${styles.galleryNav} ${styles.galleryNavPrev}`}
              onClick={() => {
                goTo(selectedIndex - 1);
                preloadMainImage(selectedIndex - 2);
              }}
              onPointerEnter={() => preloadMainImage(selectedIndex - 1)}
              onFocus={() => preloadMainImage(selectedIndex - 1)}
              aria-label={t(
                localizedTexts,
                "Tracker.Products.PreviousImages",
                "Previous image"
              )}
            >
              <span className={styles.arrow} aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </span>
            </button>
            <button
              type="button"
              className={`${styles.galleryNav} ${styles.galleryNavNext}`}
              onClick={() => {
                goTo(selectedIndex + 1);
                preloadMainImage(selectedIndex + 2);
              }}
              onPointerEnter={() => preloadMainImage(selectedIndex + 1)}
              onFocus={() => preloadMainImage(selectedIndex + 1)}
              aria-label={t(
                localizedTexts,
                "Tracker.Products.NextImages",
                "Next image"
              )}
            >
              <span className={styles.arrow} aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </span>
            </button>
          </>
        )}
      </div>

      {/* Single-row thumbnail carousel */}
      {items.length > 1 && (
        <div className={styles.thumbnailCarousel}>
          <button
            type="button"
            className={styles.carouselButton}
            onClick={handlePrevThumbnails}
            disabled={!canScrollLeft}
            aria-label={t(
              localizedTexts,
              "Tracker.Products.PreviousImages",
              "Previous images"
            )}
          >
            <ChevronLeftIcon />
          </button>

          <div ref={thumbnailListRef} className={styles.thumbnailList}>
            <div
              className={styles.thumbnailSlider}
              style={{
                transform: `translateX(-${translateX}px)`,
                gap: `${THUMBNAIL_GAP}px`,
              }}
            >
              {items.map((item, index) => {
                const isActive = index === selectedIndex;
                const thumbSrc =
                  item.kind === "image" ? item.url : item.thumbnailUrl;
                const altText =
                  item.kind === "image"
                    ? item.altText || `${productName} ${index + 1}`
                    : item.altText ||
                      t(
                        localizedTexts,
                        "Tracker.Products.PlayVideo",
                        "Play video"
                      );
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`${styles.thumbnail} ${isActive ? styles.thumbnailActive : ""}`}
                    style={{ width: thumbnailSize, height: thumbnailSize }}
                    onClick={() => setSelectedIndex(index)}
                    onPointerEnter={() => preloadMainImage(index)}
                    onFocus={() => preloadMainImage(index)}
                    aria-label={`${
                      item.kind === "video"
                        ? t(
                            localizedTexts,
                            "Tracker.Products.PlayVideo",
                            "Play video"
                          )
                        : t(
                            localizedTexts,
                            "Tracker.Products.ViewImage",
                            "View image"
                          )
                    } ${index + 1}`}
                    aria-current={isActive ? "true" : undefined}
                  >
                    {thumbSrc ? (
                      <img
                        {...fixedCmsImage(thumbSrc, {
                          width: THUMBNAIL_TARGET_WIDTH,
                        })}
                        alt={altText}
                        loading="lazy"
                      />
                    ) : (
                      <span className={styles.thumbnailPlaceholder} />
                    )}
                    {item.kind === "video" && <PlayOverlay />}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            className={styles.carouselButton}
            onClick={handleNextThumbnails}
            disabled={!canScrollRight}
            aria-label={t(
              localizedTexts,
              "Tracker.Products.NextImages",
              "Next images"
            )}
          >
            <ChevronRightIcon />
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductMediaGallery;
